import { Scenario } from '../domain/types.js';
import { ShareError, encodeShareToken, shareUrl } from '../features/persistence/share.js';

const COPIED_TEXT = 'Скопировано ✓';
const COPY_FAILED_TEXT = 'Не удалось — скопируйте вручную';
const UNSUPPORTED_TEXT = 'Ваш браузер не поддерживает создание ссылок со сценарием';
const FAILED_TEXT = 'Не удалось подготовить ссылку';
const FEEDBACK_MS = 2000;

type ShareLocation = { origin: string; pathname: string };

/** `showModal` when the browser has it; the `open` attribute otherwise (happy-dom, very old engines). */
export function openModal(dialog: HTMLDialogElement): void {
  if (dialog.open) return;
  if (typeof dialog.showModal === 'function') dialog.showModal();
  else dialog.open = true;
}

export function closeModal(dialog: HTMLDialogElement): void {
  if (typeof dialog.close === 'function') dialog.close();
  else dialog.open = false;
}

function byId<T extends HTMLElement>(id: string): T | null {
  return document.getElementById(id) as T | null;
}

/** Copies `text`; the button confirms or asks to copy by hand for two seconds. */
async function copyWithFeedback(
  button: HTMLButtonElement,
  text: string,
  field: HTMLInputElement | HTMLTextAreaElement,
): Promise<void> {
  const label = button.dataset.label ?? button.textContent ?? '';
  button.dataset.label = label;
  try {
    await navigator.clipboard.writeText(text);
    button.textContent = COPIED_TEXT;
  } catch {
    button.textContent = COPY_FAILED_TEXT;
    field.focus();
    field.select();
  }
  setTimeout(() => { button.textContent = label; }, FEEDBACK_MS);
}

/** Binds a copy button once; re-running `initShareDialog` must not stack listeners. */
function bindCopy(button: HTMLButtonElement | null, field: HTMLInputElement | HTMLTextAreaElement | null): void {
  if (!button || !field || button.dataset.bound === 'true') return;
  button.dataset.bound = 'true';
  button.addEventListener('click', () => { void copyWithFeedback(button, field.value, field); });
}

/** Binds the copy buttons of `#share-dialog` once. */
export function initShareDialog(): void {
  bindCopy(byId<HTMLButtonElement>('share-copy-link'), byId<HTMLInputElement>('share-link'));
  bindCopy(byId<HTMLButtonElement>('share-copy-code'), byId<HTMLTextAreaElement>('share-code'));
}

/** Opens `#share-dialog` for the scenario; the fields fill in once the token is built. */
// Opening another card's dialog while a token is still being built must not let the first
// token land in the second dialog.
let shareRequest = 0;

export async function openShareDialog(scenario: Scenario, location: ShareLocation = window.location): Promise<void> {
  const dialog = byId<HTMLDialogElement>('share-dialog');
  if (!dialog) return;
  const name = byId<HTMLElement>('share-scenario-name');
  const link = byId<HTMLInputElement>('share-link');
  const code = byId<HTMLTextAreaElement>('share-code');
  const error = byId<HTMLElement>('share-dialog-error');
  if (name) name.textContent = scenario.name;
  if (link) link.value = '…';
  if (code) code.value = '…';
  if (error) error.hidden = true;
  openModal(dialog);
  const request = ++shareRequest;
  try {
    const token = await encodeShareToken(scenario);
    if (request !== shareRequest) return;
    if (link) link.value = shareUrl(token, location);
    if (code) code.value = token;
  } catch (failure) {
    if (request !== shareRequest) return;
    if (link) link.value = '';
    if (code) code.value = '';
    if (error) {
      error.textContent = failure instanceof ShareError && failure.code === 'unsupported' ? UNSUPPORTED_TEXT : FAILED_TEXT;
      error.hidden = false;
    }
  }
}
